import { getOrganizationUserName } from '@interfaces/organizationUser';
import type { UserContextProps } from '@providers/userContextProvider';
import type { ReconFlagRow } from './reconFlags.types';

export type ReconFlagNote = NonNullable<ReconFlagRow['notes']>[number];

/** Display name sent to flag-service when appending a note. */
export const getReconFlagNoteAuthorName = (
  userContext:
    | Pick<UserContextProps, 'firstName' | 'lastName' | 'email'>
    | undefined,
): string => {
  const name = getOrganizationUserName(userContext)?.trim();
  if (name) {
    return name;
  }
  return userContext?.email?.trim() ?? '';
};

export const buildUpdateFlagNotesInput = (
  flagId: string,
  notes: string,
  author: string,
) => {
  const trimmedAuthor = author.trim();
  return {
    flagId,
    notes: notes.trim(),
    ...(trimmedAuthor !== '' && { author: trimmedAuthor }),
  };
};

/** Newest notes first for display. */
export const sortFlagNotesNewestFirst = (
  notes: readonly ReconFlagNote[] | null | undefined,
): ReconFlagNote[] =>
  [...(notes ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

const newestNoteTimestamp = (
  notes: readonly ReconFlagNote[] | null | undefined,
): number => {
  const newest = sortFlagNotesNewestFirst(notes)[0];
  return newest ? new Date(newest.createdAt).getTime() : 0;
};

/** True when `candidate` is at least as fresh as `baseline` (newest note time, then count). */
export const areFlagNotesAtLeastAsFresh = (
  candidate: readonly ReconFlagNote[] | null | undefined,
  baseline: readonly ReconFlagNote[] | null | undefined,
): boolean => {
  const candidateNewest = newestNoteTimestamp(candidate);
  const baselineNewest = newestNoteTimestamp(baseline);
  if (candidateNewest !== baselineNewest) {
    return candidateNewest > baselineNewest;
  }
  return (candidate?.length ?? 0) >= (baseline?.length ?? 0);
};

/** True when the draft contains a note that has not been saved yet. */
export const hasUnsavedFlagNoteDraft = (draft: string): boolean =>
  draft.trim() !== '';
